import './bootstrap';
import { initCalendar } from './Planning/calendar';
import { initAttendance } from './Planning/attendance';
import { initCoverage } from './Planning/coverage';
import { initQuickFilters } from './Planning/quickFilters';
import { initToolbar } from './Planning/toolbar';
import { initExportModal } from './Planning/exportModal';
import { initPublishModal } from './Planning/publishModal';
import { initPayroll } from './Planning/payroll';
import { initAdvisor } from './Planning/advisor';
import { initWeekTemplates } from './Planning/weekTemplates';
import { initSettings } from './Planning/settings';
import { enhanceSelects } from './Planning/selects';
import { initTwoFactor } from './Planning/twoFactor';
import { initUserMenu } from './Planning/userMenu';
import { initPrefetch } from './Planning/prefetch';
import { initSoftNav } from './Planning/softNav';

import.meta.glob(['../images/**']);

/*
| Dark-mode toggle. The initial class is applied by an inline <head> script
| (see layouts/standard.blade.php) to avoid a flash; here we only wire the
| toggle buttons and persist the choice.
*/
const THEME_KEY = 'planning-theme';
const root = document.documentElement;

const setMode = (mode: 'light' | 'dark'): void => {
    root.classList.toggle('dark', mode === 'dark');
    localStorage.setItem(THEME_KEY, mode);
};

// Per-page inits — re-runnable after a soft-nav #screen swap. Everything here binds
// to #screen content (element listeners GC with the swap) or uses the per-screen
// AbortSignal (document/window listeners). Global, once-only wiring lives in init().
const initScreen = (): void => {
    initCalendar();
    initAttendance();
    initCoverage();
    initQuickFilters();
    initToolbar();
    initExportModal();
    initPublishModal();
    initPayroll();
    initWeekTemplates();
    initSettings();
    initTwoFactor();
    initClaimBoxes();
    enhanceSelects();

    // "Beru na vědomí" on the published-schedule strip (my-schedule).
    document.querySelectorAll<HTMLElement>('[data-ack-url]').forEach((btn) => {
        btn.addEventListener('click', async () => {
            btn.setAttribute('disabled', 'true');
            try {
                await axios().post(btn.dataset.ackUrl);
                location.reload();
            } catch (e: any) {
                btn.removeAttribute('disabled');
                alert(e?.response?.data?.message ?? 'Error');
            }
        });
    });

    // Withdraw a pending claim from the "Moje přihlášky" panel (my-schedule).
    document.querySelectorAll<HTMLElement>('[data-withdraw-url]').forEach((btn) => {
        btn.addEventListener('click', async () => {
            btn.setAttribute('disabled', 'true');
            try {
                await axios().post(btn.dataset.withdrawUrl);
                location.reload();
            } catch (e: any) {
                btn.removeAttribute('disabled');
                alert(e?.response?.data?.message ?? 'Error');
            }
        });
    });
};

const init = (): void => {
    // Global, once-only: these live in the persistent topbar/overlay/advisor (outside
    // #screen), so they must not be re-bound on a soft-nav swap.
    document.querySelectorAll<HTMLElement>('[data-theme-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => {
            setMode(root.classList.contains('dark') ? 'light' : 'dark');
        });
    });
    initMobileNav();
    initUserMenu();
    initPrefetch();
    initAdvisor();
    initSoftNav(initScreen);

    initScreen();
};

const axios = (): any => (window as any).axios;

/*
| Mobile fullscreen overlay navigation (≤767px). Focus is trapped while open,
| Escape closes, and focus returns to the toggle that opened it.
*/
const initMobileNav = (): void => {
    const toggle = document.querySelector<HTMLButtonElement>('[data-mobile-nav-toggle]');
    const overlay = document.getElementById('nav-overlay');
    if (!toggle || !overlay) {
        return;
    }
    const close = (): void => {
        overlay.hidden = true;
        overlay.classList.remove('nav-overlay--in');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.focus();
    };
    const open = (): void => {
        overlay.hidden = false;
        overlay.classList.add('nav-overlay--in');
        toggle.setAttribute('aria-expanded', 'true');
        overlay.querySelector<HTMLElement>('a, button')?.focus();
    };
    toggle.addEventListener('click', () => (overlay.hidden ? open() : close()));
    // Soft navigation swaps the screen without a reload, so close the overlay when
    // a nav link is followed (only if open — avoids stealing focus on desktop).
    document.addEventListener('planning:navigated', () => {
        if (!overlay.hidden) {
            close();
        }
    });
    overlay.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            close();
            return;
        }
        if (e.key !== 'Tab') {
            return;
        }
        const items = Array.from(overlay.querySelectorAll<HTMLElement>('a, button')).filter((el) => !el.hasAttribute('disabled'));
        if (items.length === 0) {
            return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    });
};

/*
| Open-shifts request box. Claiming creates a pending request a manager later
| assigns; the box reflects state (request <-> awaiting + withdraw) without a
| reload. 409 = duplicate/taken.
*/
const initClaimBoxes = (): void => {
    document.querySelectorAll<HTMLElement>('[data-claim-box]').forEach(renderClaimBox);
};

function renderClaimBox(box: HTMLElement): void {
    if (box.dataset.claimId) {
        box.innerHTML =
            '<span class="pl-badge" style="background: var(--state-ok-soft); color: var(--state-ok)">✓ Přihlášeno · čeká</span>' +
            '<button type="button" class="pl-btn pl-btn-ghost" data-withdraw>Vzít zpět</button>';
        box.querySelector('[data-withdraw]')!.addEventListener('click', () => resolveClaimBox(box, 'withdraw'));
        return;
    }
    box.innerHTML = '<button type="button" class="pl-btn pl-btn-primary" data-claim dusk="claim-shift">Přihlásit se</button>';
    box.querySelector('[data-claim]')!.addEventListener('click', () => resolveClaimBox(box, 'claim'));
}

async function resolveClaimBox(box: HTMLElement, action: 'claim' | 'withdraw'): Promise<void> {
    const btn = box.querySelector('button')!;
    btn.setAttribute('disabled', 'true');
    try {
        if (action === 'claim') {
            const { data } = await axios().post(box.dataset.claimUrl);
            box.dataset.claimId = String(data.claim.id);
        } else {
            await axios().post(`${box.dataset.withdrawBase}/${box.dataset.claimId}/withdraw`);
            delete box.dataset.claimId;
        }
        renderClaimBox(box);
    } catch (e: any) {
        btn.removeAttribute('disabled');
        alert(e?.response?.data?.message ?? 'Error');
    }
}

// Deferred module scripts can execute after DOMContentLoaded has already fired;
// run immediately when the DOM is parsed, otherwise wait for the event.
if (document.readyState === 'loading') {
    window.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
